import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BatchStatusBadge } from "@/components/StatusBadge";
import { Truck, FilePlus2, ChevronRight } from "lucide-react";
import { useWarehousePermissions } from "@/hooks/useWarehousePermissions";

export default function OutboundBatches() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can } = useWarehousePermissions();

  useEffect(() => {
    api.get("/warehouse/batches")
      .then(r => setRows(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="wh-batches-page" className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-1">/ Outbound</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Outbound Batches</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">Group sorted packages, ship them out, and stage for last-mile riders.</p>
        </div>
        <Link to="/warehouse/batches/new" className={can("create_batch") ? "" : "hidden"}>
          <Button data-testid="wh-batches-new" className="bg-cyan-400 text-slate-950 hover:bg-cyan-300">
            <FilePlus2 className="w-4 h-4 mr-2" /> New Batch
          </Button>
        </Link>
      </div>
      {loading ? (
        <div className="py-14 text-center text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-14 text-center bg-slate-100/30 dark:bg-slate-900/30">
          <Truck className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="font-display text-base text-slate-700 dark:text-slate-300 mb-1">No batches yet</div>
          <div className="text-xs text-slate-500">Create your first outbound batch above.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(b => (
            <Link
              key={b.id}
              to={`/warehouse/batches/${b.id}`}
              data-testid={`wh-batch-${b.batch_code}`}
              className="block border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40 hover:bg-slate-100 hover:dark:bg-slate-900 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono-tech text-xs text-cyan-300">{b.batch_code}</div>
                  <div className="font-display text-lg font-semibold truncate">{b.batch_name}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 truncate mt-0.5">
                    {b.destination_area || "—"} · {b.total_packages || 0} packages · {b.created_by_name}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <BatchStatusBadge status={b.status} />
                  <ChevronRight className="w-4 h-4 text-slate-500 dark:text-slate-600" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
